import {Observable} from 'rxjs';
import {Injectable} from "@angular/core";
import {environment} from "../../environments/environment";
import {BalanceTeamsResult, RecruitmentCampaign, Event, EventGameResult, EventInviteLinkResponse, EventPlayerBlock, InactiveEventPlayer, RemoveInactivePlayersResult} from "../models/event";
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import {PlayerEventInfo, PlayerEventInfoStatus, TeamMemberType} from "../models/playerEventInfo";
import {Player} from "../models/player";
import { map, tap, finalize, shareReplay } from 'rxjs/operators';
import { AnalyticsService } from './analytics.service';
import { PlayerService } from './player.service';

export interface InvitationDispatchResult {
  sentCount: number;
  skippedCount: number;
}

@Injectable()
export class EventService {
  private apiUrl: string = environment.API_BASE_URL + '/event';
  private richApiUrl: string = environment.API_BASE_URL + '/rich-event';
  private eventsApiUrl: string = environment.API_BASE_URL + '/events';
  private playerEventInfoStatus: PlayerEventInfoStatus = new PlayerEventInfoStatus();
  private teamMemberType: TeamMemberType = new TeamMemberType();

  constructor(private authHttp: HttpClient, private analytics: AnalyticsService, private playerService: PlayerService) {
  }

  private static getRequestOptions() {
    return {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    };
  }

  public createEvent(body: Object): Observable<Event> {
    let bodyString = JSON.stringify(body);
    return this.authHttp.post<Event>(`${this.apiUrl}`, bodyString, EventService.getRequestOptions()).pipe(
      map((event) => this.normalizeEventDateFields(event)),
      tap((event) => this.analytics.track("match_created", {
        screen: "match_detail",
        entityType: "event",
        entityId: event && event.id
      }))
    );
  }

  public changeManager(eventId : string, playerId: string): Observable<string> {
    return this.authHttp.post<string>(`${this.apiUrl}/${eventId}/changeManager/${playerId}`, EventService.getRequestOptions());
  }

  public notifyPlayers(event: Event, teamMemberType: string): Observable<InvitationDispatchResult> {
    let bodyString = JSON.stringify(event);

    const requestOptions = {
      headers: new HttpHeaders({'Content-Type': 'application/json'}),
      params: teamMemberType ? new HttpParams().set('teamMemberType', teamMemberType) : new HttpParams()
    };

    return this.authHttp.put<InvitationDispatchResult>(`${this.apiUrl}/notifications`, bodyString, requestOptions);
  }

  public invitePlayer(playerEventInfo: PlayerEventInfo): Observable<PlayerEventInfo> {
    let bodyString = JSON.stringify(playerEventInfo);
    return this.authHttp.put<PlayerEventInfo>(`${this.apiUrl}/notifications/player/confirmation/${playerEventInfo['id']}`, bodyString, EventService.getRequestOptions()).pipe(
      tap(() => this.analytics.track("match_invite_sent", {
        screen: "match_players",
        entityType: "event_player_info",
        entityId: playerEventInfo && playerEventInfo.id
      }))
    );
  }

  public notifyPlayer(playerEventInfo: PlayerEventInfo): Observable<PlayerEventInfo> {
    let bodyString = JSON.stringify(playerEventInfo);
    return this.authHttp.put<PlayerEventInfo>(`${this.apiUrl}/notifications/player/notification/${playerEventInfo['id']}`, bodyString, EventService.getRequestOptions());
  }

  public notifyNotAnsweredEventPlayers(eventId : string ): Observable<InvitationDispatchResult> {
    return this.authHttp.post<InvitationDispatchResult>(`${this.apiUrl}/notifications/event/notAnswered/${eventId}`, EventService.getRequestOptions());
  }

  public notifyAllOpenProfiles(eventId: string): Observable<string> {
    return this.authHttp.post<string>(`${this.apiUrl}/notifyAllOpenProfiles/${eventId}`, EventService.getRequestOptions());
  }

  public notifyTeammateOpenProfiles(eventId: string): Observable<string> {
    return this.authHttp.post<string>(`${this.apiUrl}/notifyEventmateOpenProfiles/${eventId}`, EventService.getRequestOptions());
  }

  public fetchHistory(originalEventId: string): Observable<Event[]> {
    return this.authHttp.get<Event[]>(`${this.apiUrl}/archived/${originalEventId}`, EventService.getRequestOptions()).pipe(
      map((events) => this.normalizeEventDateFieldsList(events))
    );
  }

  public updateEvent(body: Object): Observable<Event> {
    let bodyString = JSON.stringify(body);
    return this.authHttp.put<Event>(`${this.apiUrl}/${body['id']}`, bodyString, EventService.getRequestOptions()).pipe(
      map((event) => this.normalizeEventDateFields(event)),
      tap((event) => this.analytics.track("match_edited", {
        screen: "match_detail",
        entityType: "event",
        entityId: event && event.id ? event.id : body && body["id"]
      }))
    );
  }

  public updateRichEvent(body: Object): Observable<any> {
    let bodyString = JSON.stringify(body);
    return this.authHttp.put<any>(`${this.apiUrl}/rich/${body['id']}`, bodyString, EventService.getRequestOptions()).pipe(
      map((event) => this.normalizeEventDateFields(event)),
      tap((event) => this.analytics.track("match_edited", {
        screen: "match_players",
        entityType: "event",
        entityId: event && event.id ? event.id : body && body["id"]
      }))
    );
  }

  public removeSingleEventOccurrenceById(id: string): Observable<Event> {
    return this.authHttp.delete<Event>(`${this.apiUrl}/${id}`);
  }

  public removeAllEventOccurrenceByOriginalId(originalId: string): Observable<Event> {
    return this.authHttp.delete<Event>(`${this.apiUrl}/deleteAllEventsByOriginalId/${originalId}`);
  }

  public removeManagedEventSeries(eventId: string): Observable<void> {
    return this.authHttp.delete<void>(`${this.apiUrl}/${eventId}/managed-series`, EventService.getRequestOptions()).pipe(
      tap(() => this.analytics.track("match_deleted", {
        screen: "match_players",
        entityType: "event",
        entityId: eventId
      }))
    );
  }

  public cancelEvent(eventId: string, reason: string): Observable<any> {
    return this.authHttp.post<any>(`${this.apiUrl}/${eventId}/cancel`, JSON.stringify({ reason: reason || null }), EventService.getRequestOptions()).pipe(
      map((event) => this.normalizeEventDateFields(event)),
      tap(() => this.analytics.track("match_cancelled", {
        screen: "match_players",
        entityType: "event",
        entityId: eventId
      }))
    );
  }

  public uncancelEvent(eventId: string): Observable<any> {
    return this.authHttp.post<any>(`${this.apiUrl}/${eventId}/uncancel`, null, EventService.getRequestOptions()).pipe(
      map((event) => this.normalizeEventDateFields(event)),
      tap(() => this.analytics.track("match_uncancelled", {
        screen: "match_players",
        entityType: "event",
        entityId: eventId
      }))
    );
  }

  public getConfirmedCount(event: Event): number {
    if (event.playerEventInfos == null || event.playerEventInfos.length === 0) {
      return 0;
    }

    let count: number = 0;
    for (let playerEventInfo of event.playerEventInfos) {
      if (playerEventInfo.status === this.playerEventInfoStatus.CONFIRMED) {
        count++;
      }
    }
    return count;
  }

  public getAllActiveEvents(): Observable<Event[]> {
    return this.authHttp.get<Event[]>(`${this.apiUrl}/active`, EventService.getRequestOptions()).pipe(
      map((events) => this.normalizeEventDateFieldsList(events))
    );
  }

  public getEvent(eventId: string) : Observable<Event>{
    return this.authHttp.get<Event>(`${this.apiUrl}/${eventId}`, EventService.getRequestOptions()).pipe(
      map((event) => this.normalizeEventDateFields(event)),
      tap((event) => this.analytics.track("match_viewed", {
        screen: "match_detail",
        entityType: "event",
        entityId: event && event.id ? event.id : eventId
      }))
    );
  }

  private richRequests = new Map<string, Observable<Event>>();

  public getRichEvent(eventId: string): Observable<Event> {
    const accountId = this.playerService.getCurrentPlayer()?.id;
    const key = `${accountId || ''}:${eventId}`;
    let request = this.richRequests.get(key);
    if (!request) {
      request = this.authHttp.get<Event>(`${this.richApiUrl}/${eventId}`, EventService.getRequestOptions()).pipe(
        tap(event => this.analytics.track("match_viewed", {
          screen: "match_detail", entityType: "event", entityId: event.id || eventId
        })),
        finalize(() => this.richRequests.delete(key)),
        shareReplay({bufferSize: 1, refCount: true})
      );
      this.richRequests.set(key, request);
    }
    return request.pipe(map(event => {
      if (this.playerService.getCurrentPlayer()?.id !== accountId) throw new Error('Session changed');
      // Each screen owns its mutable editor state; only the in-flight HTTP request is shared.
      return this.normalizeEventDateFields(structuredClone(event));
    }));
  }

  public getAllActiveEventsNearBy(latitude: number, longitude: number, distanceInKm: number): Observable<Event[]> {
    return this.authHttp.get<Event[]>(`${this.richApiUrl}/active/geo?latitude=${latitude}&longitude=${longitude}&distanceInKm=${distanceInKm != null ? distanceInKm : 50}`, EventService.getRequestOptions()).pipe(
      map((events) => this.normalizeEventDateFieldsList(events))
    );
  }

  public getRecruitmentCampaign(eventId: string): Observable<RecruitmentCampaign> {
    return this.authHttp.get<RecruitmentCampaign>(`${this.apiUrl}/${eventId}/recruitment-campaign`);
  }

  public launchRecruitmentCampaign(eventId: string): Observable<RecruitmentCampaign> {
    return this.authHttp.post<RecruitmentCampaign>(`${this.apiUrl}/${eventId}/recruitment-campaign/launch`, {});
  }

  public getMyHomeEvents(): Observable<Event[]> {
    return this.authHttp.get<Event[]>(`${this.richApiUrl}/active/my/home`).pipe(map(events => this.normalizeEventDateFieldsList(events)));
  }

  public getNearbyHomeEvents(latitude: number, longitude: number): Observable<Event[]> {
    return this.authHttp.get<Event[]>(`${this.richApiUrl}/active/geo/home`, {
      params: new HttpParams().set('latitude', latitude).set('longitude', longitude).set('distanceInKm', 50)
    }).pipe(map(events => this.normalizeEventDateFieldsList(events)));
  }

  public getMyActiveRichEvents(): Observable<Event[]> {
    return this.authHttp.get<Event[]>(`${this.richApiUrl}/active/my`, EventService.getRequestOptions()).pipe(
      map((events) => this.normalizeEventDateFieldsList(events))
    );
  }

  public requestToLeaveEvent(event: Event, player: Player): Observable<Response> {
    return this.authHttp.post<Response>(`${this.apiUrl}/${event.id}/leave/player/${player.id}`, EventService.getRequestOptions());
  }

  public requestToJoinEvent(event: Event, player: Player): Observable<Response> {
    return this.authHttp.post<Response>(`${this.apiUrl}/${event.id}/join/player/${player.id}`, EventService.getRequestOptions()).pipe(
      tap(() => this.analytics.track("match_joined", {
        screen: "matches_list",
        entityType: "event",
        entityId: event && event.id,
        properties: { responseType: "join_request" }
      }))
    );
  }

  public ensureInviteLink(eventId: string): Observable<EventInviteLinkResponse> {
    return this.authHttp.post<EventInviteLinkResponse>(`${this.apiUrl}/${eventId}/invite-link`, {}, EventService.getRequestOptions());
  }

  public clearTeams(eventId: string): Observable<void> {
    return this.authHttp.delete<void>(`${this.eventsApiUrl}/${eventId}/balance-teams`, EventService.getRequestOptions());
  }

  public moveBalancedPlayer(eventId: string, playerId: string, jersey: 'WHITE' | 'BLACK', rosterPosition: number): Observable<void> {
    return this.authHttp.put<void>(`${this.eventsApiUrl}/${eventId}/balance-teams/${playerId}`, {jersey, rosterPosition}, EventService.getRequestOptions());
  }

  public balanceTeams(eventId: string): Observable<BalanceTeamsResult> {
    return this.authHttp.post<BalanceTeamsResult>(`${this.eventsApiUrl}/${eventId}/balance-teams`, {}, EventService.getRequestOptions()).pipe(
      tap(() => this.analytics.track("team_balance_generated", {
        screen: "match_players",
        entityType: "event",
        entityId: eventId
      }))
    );
  }

  public getGameResults(eventId: string): Observable<EventGameResult[]> {
    return this.authHttp.get<EventGameResult[]>(`${this.eventsApiUrl}/${eventId}/game-results`, EventService.getRequestOptions());
  }

  public saveGameResults(eventId: string, gameResults: EventGameResult[]): Observable<EventGameResult[]> {
    return this.authHttp.put<EventGameResult[]>(
      `${this.eventsApiUrl}/${eventId}/game-results`,
      JSON.stringify(gameResults),
      EventService.getRequestOptions()
    ).pipe(
      tap(() => this.analytics.track("match_result_saved", {
        screen: "match_history",
        entityType: "event",
        entityId: eventId,
        properties: { rounds: gameResults ? gameResults.length : 0 }
      }))
    );
  }

  public getInactivePlayersForCleanup(eventId: string, inactiveMonths: number): Observable<InactiveEventPlayer[]> {
    return this.authHttp.get<InactiveEventPlayer[]>(
      `${this.apiUrl}/${eventId}/cleanup/inactivePlayers`,
      {
        headers: new HttpHeaders({'Content-Type': 'application/json'}),
        params: new HttpParams().set('inactiveMonths', String(inactiveMonths))
      }
    );
  }

  public removeInactivePlayers(eventId: string, playerIds: string[]): Observable<RemoveInactivePlayersResult> {
    return this.authHttp.post<RemoveInactivePlayersResult>(
      `${this.apiUrl}/${eventId}/cleanup/removePlayers`,
      JSON.stringify({playerIds}),
      EventService.getRequestOptions()
    );
  }

  public getBlockedPlayers(eventId: string): Observable<EventPlayerBlock[]> {
    return this.authHttp.get<EventPlayerBlock[]>(`${this.apiUrl}/${eventId}/blocks`, EventService.getRequestOptions());
  }

  public blockPlayer(eventId: string, playerId: string): Observable<EventPlayerBlock> {
    return this.authHttp.post<EventPlayerBlock>(`${this.apiUrl}/${eventId}/blocks/${playerId}`, null, EventService.getRequestOptions());
  }

  public unblockPlayer(eventId: string, playerId: string): Observable<void> {
    return this.authHttp.delete<void>(`${this.apiUrl}/${eventId}/blocks/${playerId}`, EventService.getRequestOptions());
  }

  public invitePlayersToJoinNogosh(event: Event, emails: string): Observable<Response> {
    return this.authHttp.post<Response>(`${this.apiUrl}/invitePlayersToJoinNogosh/?eventId=${event.id}&emails=${emails}`, EventService.getRequestOptions()).pipe(
      tap(() => this.analytics.track("match_invite_sent", {
        screen: "match_players",
        entityType: "event",
        entityId: event && event.id
      }))
    );
  }

  private normalizeEventDateFieldsList(events: Event[]): Event[] {
    return (events || []).map((event) => this.normalizeEventDateFields(event));
  }

  private normalizeEventDateFields<T extends Event>(event: T): T {
    if (!event) {
      return event;
    }

    if (!event.nextOccurrenceTimestamp && event.nextOccurrence) {
      event.nextOccurrenceTimestamp = event.nextOccurrence as any;
    }

    this.normalizeRichEventFields(event);
    this.normalizeEventPlayerAvatars(event);

    if (event.season1StartDate) {
      event.season1StartDate = new Date(event.season1StartDate);
    }
    if (event.season2StartDate) {
      event.season2StartDate = new Date(event.season2StartDate);
    }
    if (event.seasonStartDates) {
      event.seasonStartDates = event.seasonStartDates.map((date) => new Date(date));
    }
    if (event.setupCompletedAt) {
      event.setupCompletedAt = new Date(event.setupCompletedAt);
    }
    if (event.managerLastViewedAt) {
      event.managerLastViewedAt = new Date(event.managerLastViewedAt);
    }
    if (event.cancelledAt) {
      event.cancelledAt = new Date(event.cancelledAt);
    }

    return event;
  }

  private normalizeEventPlayerAvatars(event: Event): void {
    PlayerService.normalizePlayerAvatar(event.manager);

    if (event.playerEventInfos) {
      event.playerEventInfos.forEach((playerEventInfo) => {
        if (playerEventInfo) {
          PlayerService.normalizePlayerAvatar(playerEventInfo.player);
        }
      });
    }

    const richEvent: any = event;
    if (richEvent.participants) {
      richEvent.participants.forEach((participant) => PlayerService.normalizePlayerAvatar(participant));
    }
  }

  private normalizeRichEventFields(event: Event): void {
    const richEvent: any = event;

    if (event.sosUsed === undefined && richEvent.sos !== undefined) {
      event.sosUsed = richEvent.sos;
    }

    if (event.sosUsed === undefined && richEvent.isSos !== undefined) event.sosUsed = richEvent.isSos;

    if (event.teammateSosUsed === undefined) {
      event.teammateSosUsed = !!richEvent.teammateSosUsed;
    }

    if (event.confirmedCount === undefined && event.attendance && event.attendance.confirmed !== undefined) {
      event.confirmedCount = event.attendance.confirmed;
    }

    if (event.maxPlayers === undefined && event.attendance && event.attendance.max !== undefined) {
      event.maxPlayers = event.attendance.max;
    }

    if (event.participation) {
      event.currentPlayerInviteStatus = event.participation.status;
      event.viewedByPlayer = event.participation.teamMemberType !== this.teamMemberType.CANDIDATE;
      event.viewedByCandidate = event.participation.teamMemberType === this.teamMemberType.CANDIDATE;
    }

    if ((!event.playerEventInfos || event.playerEventInfos.length === 0) && event.participants && event.participants.length > 0) {
      event.playerEventInfos = event.participants
        .filter((participant: any) => participant && participant.participation)
        .map((participant: any) => {
          const playerEventInfo = new PlayerEventInfo();
          playerEventInfo.eventId = event.id;
          playerEventInfo.id = participant.participation.playerEventInfoId;
          playerEventInfo.player = participant;
          playerEventInfo.status = participant.participation.status;
          playerEventInfo.jersey = participant.participation.jersey;
          playerEventInfo.rosterPosition = participant.participation.rosterPosition;
          playerEventInfo.teamAssigned = participant.participation.teamAssigned;
          playerEventInfo.teamMemberType = participant.participation.teamMemberType;
          playerEventInfo.source = participant.participation.source;
          playerEventInfo.joinedAt = participant.participation.joinedAt;
          playerEventInfo.statusModifiedByPlayerId = participant.participation.modifier?.playerId;
          playerEventInfo.statusModifiedByPlayerEmail = participant.participation.modifier?.email;
          playerEventInfo.modificationDate = participant.participation.modificationDate;
          playerEventInfo.invitationSentAt = participant.participation.invitationSentAt;
          playerEventInfo.reminderSentAt = participant.participation.reminderSentAt;
          return playerEventInfo;
        });
    }
  }

}
